import { useEffect, useState } from 'react'
import { getToken } from './auth'
import LoginGate from './components/LoginGate'
import Shell from './components/Shell'
import FocusPanel from './components/FocusPanel'
import ActiveSessions from './components/ActiveSessions'
import TaskList from './components/TaskList'
import IssuesPanel from './components/IssuesPanel'
import InfraPanel from './components/InfraPanel'
import InboxTriage from './components/InboxTriage'
import CalendarStrip from './components/CalendarStrip'
import HomePanel from './components/home/HomePanel'
import HeroStrip from './components/HeroStrip'
import SortableLayout, { type PanelLayout } from './components/SortableLayout'
import Mercedes from './components/mercedes/Mercedes'
import CommandPalette from './components/CommandPalette'
import ActivityTimeline from './components/ActivityTimeline'
import UrgentTasks from './components/UrgentTasks'
import { FocusModeProvider } from './context/FocusModeContext'
import { DemoModeProvider } from './context/DemoModeContext'
import DashboardLayout from './components/DashboardLayout'

const DEFAULT_LAYOUT: PanelLayout = {
  col1:    ['urgent', 'infra', 'focus', 'activity', 'active-sessions'],
  col2:    ['calendar', 'inbox', 'issues', 'tasks'],
  rowFull: [],
}

type Tab = 'work' | 'home'
const TAB_KEY = 'jamie-dashboard:active-tab'

export default function App() {
  const [authed, setAuthed] = useState<boolean>(() => Boolean(getToken()))
  const [tab, setTab] = useState<Tab>(() => {
    const v = localStorage.getItem(TAB_KEY)
    return v === 'home' ? 'home' : 'work'
  })

  function changeTab(next: Tab) {
    setTab(next)
    try { localStorage.setItem(TAB_KEY, next) } catch { /* ignore */ }
  }

  useEffect(() => {
    const onClear = () => setAuthed(false)
    window.addEventListener('auth-cleared', onClear)
    // Restore demo-mode dataset attribute on mount
    if (localStorage.getItem('jamie-dashboard:demo-mode') === '1') {
      document.body.dataset.demoMode = 'true'
    }
    return () => window.removeEventListener('auth-cleared', onClear)
  }, [])

  // /v2-preview — render the designer's reference DashboardLayout (demo data).
  // No auth required; entirely client-side; useful for showing the new visual
  // language to clients / on a phone without signing in.
  const isV2Preview = window.location.pathname.startsWith('/v2-preview')
  if (isV2Preview) {
    return (
      <FocusModeProvider>
        <DemoModeProvider>
          <div className="dashboard-bg" style={{ minHeight: '100vh', padding: '20px' }}>
            <div style={{ maxWidth: 1440, margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                            marginBottom: 16, paddingBottom: 12,
                            borderBottom: '1px solid var(--border-soft)' }}>
                <span className="text-h2" style={{ color: 'var(--accent)' }}>v2 design preview</span>
                <a href="/" style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>← back to live dashboard</a>
              </div>
              <DashboardLayout />
            </div>
          </div>
        </DemoModeProvider>
      </FocusModeProvider>
    )
  }

  if (!authed) return <LoginGate onAuthed={() => setAuthed(true)} />

  return (
    <FocusModeProvider>
    <DemoModeProvider>
    <Shell>
      <nav className="tab-strip" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'work'}
                className={`tab ${tab === 'work' ? 'active' : ''}`}
                onClick={() => changeTab('work')}>
          Work
        </button>
        <button type="button" role="tab" aria-selected={tab === 'home'}
                className={`tab ${tab === 'home' ? 'active' : ''}`}
                onClick={() => changeTab('home')}>
          Home · Claire
        </button>
      </nav>

      {tab === 'work' && (
        <>
          <HeroStrip />
          <SortableLayout
            defaultLayout={DEFAULT_LAYOUT}
            panels={{
              'urgent':          <UrgentTasks />,
              'infra':           <InfraPanel />,
              'focus':           <FocusPanel />,
              'activity':        <ActivityTimeline />,
              'active-sessions': <ActiveSessions />,
              'calendar':        <CalendarStrip />,
              'inbox':           <InboxTriage />,
              'issues':          <IssuesPanel />,
              'tasks':           <TaskList />,
            }}
          />
        </>
      )}

      {tab === 'home' && (
        <div className="home-tab">
          <HomePanel />
        </div>
      )}

      <Mercedes />
      <CommandPalette onChangeTab={changeTab} />
    </Shell>
    </DemoModeProvider>
    </FocusModeProvider>
  )
}
